import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Truck, MapPin, Store, PackageCheck, Ship, ArrowRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { QuoteBox } from "@/components/QuoteBox";
import { VehicleCard } from "@/components/VehicleCard";
import { COMPANY, IMAGES, SERVICES } from "@/lib/company";
import { fetchVehicles } from "@/lib/api";

const SERVICE_ICONS = {
  "vehicle-transportation": Truck,
  interstate: MapPin,
  dealer: Store,
  logistics: PackageCheck,
  export: Ship,
};

export default function Home() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchVehicles().then((all) => setFeatured(all.filter((v) => v.availability === "Available").slice(0, 3))).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-slate-900" data-testid="home-hero">
        <img
          src={IMAGES.heroTransport}
          alt="Enclosed auto transport carrier loaded with vehicles"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:py-28">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Vehicle Transport, Sales &amp; Automotive Logistics
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed">
              {COMPANY.name} provides vehicle transportation and automotive services for
              individuals, dealers, and businesses. We coordinate long-distance vehicle transport,
              automotive logistics, vehicle purchasing, resale, and export-related services.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate("/catalog")}
                data-testid="hero-browse-vehicles-btn"
                className="inline-flex items-center justify-center rounded-md bg-white px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition-colors"
              >
                View Vehicles
              </button>
              <button
                onClick={() => navigate("/contact?type=Transportation%20Quote")}
                data-testid="hero-request-quote-btn"
                className="inline-flex items-center justify-center rounded-md bg-blue-700 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-600 transition-colors"
              >
                Request Transport Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Quote box */}
      <section className="relative z-10 mx-auto max-w-7xl px-4 -mt-10 sm:-mt-12" data-testid="home-quote-section">
        <QuoteBox />
      </section>

      {/* Owner & Management */}
      <section className="mx-auto max-w-7xl px-4 pt-14" data-testid="home-owner">
        <div className="border-l-4 border-blue-800 pl-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Owner &amp; Management</p>
          <h2 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Andrii Veremchuk — Owner
          </h2>
          <div className="mt-4 max-w-3xl space-y-4 text-slate-700 leading-relaxed">
            <p>
              {COMPANY.name} is owner-operated and managed by Andrii Veremchuk. The company was
              established in Florida in {COMPANY.established} and focuses on vehicle transportation,
              automotive logistics, vehicle purchasing, resale, and related automotive services.
            </p>
            <p>
              Our approach is straightforward: clear communication, practical automotive solutions,
              and dependable service for individual customers, dealers, and other businesses.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="mx-auto max-w-7xl px-4 py-16" data-testid="home-services">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Transportation Services
            </h2>
            <p className="mt-2 text-slate-600 max-w-2xl">
              Vehicle-focused transport and logistics for individual owners, dealerships, and
              automotive businesses.
            </p>
          </div>
          <Link to="/services" className="text-sm font-semibold text-blue-800 hover:underline inline-flex items-center gap-1">
            All services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
          {SERVICES.map((s) => {
            const Icon = SERVICE_ICONS[s.key];
            return (
              <div key={s.key} className="bg-white p-6" data-testid={`home-service-${s.key}`}>
                <Icon className="h-6 w-6 text-blue-800" />
                <h3 className="mt-3 font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
          <div className="bg-slate-900 p-6 flex flex-col justify-center">
            <p className="text-white font-semibold">Need a vehicle moved?</p>
            <p className="text-slate-400 text-sm mt-1">Send pickup and delivery details for a quote.</p>
            <Link to="/contact?type=Transportation%20Quote" className="mt-3 text-sm font-semibold text-blue-300 hover:text-white inline-flex items-center gap-1">
              Request a quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured vehicles */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="home-featured">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Available Vehicles
            </h2>
            <Link to="/catalog" className="text-sm font-semibold text-blue-800 hover:underline inline-flex items-center gap-1" data-testid="home-view-catalog-link">
              Browse full catalog <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((v) => (
              <VehicleCard key={v.stock_number} v={v} />
            ))}
          </div>
        </div>
      </section>

      {/* Company info + contact */}
      <section className="mx-auto max-w-7xl px-4 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-testid="home-company">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">About the Company</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {COMPANY.name} is a Florida-based automotive transportation and vehicle services
            company established in {COMPANY.established}. We work with individuals, dealerships,
            wholesalers, and other businesses that need dependable vehicle transportation and
            automotive logistics.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            The company also participates in vehicle purchasing and resale activities and assists
            with transportation related to vehicle exports.
          </p>
          <Link to="/about" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-800 hover:underline">
            More about us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="border border-slate-200 rounded-md p-6 bg-white" data-testid="home-contact-card">
          <h3 className="font-semibold text-slate-900">Contact Details</h3>
          <address className="mt-3 not-italic text-slate-700 space-y-1">
            <div>{COMPANY.name}</div>
            <div>{COMPANY.address_line1}</div>
            <div>{COMPANY.address_line2}</div>
            <div>{COMPANY.country}</div>
          </address>
          <div className="mt-4 space-y-1 text-slate-700">
            <div><span className="text-slate-400">Phone: </span><a className="text-blue-800 hover:underline" href={`tel:${COMPANY.phoneHref}`}>{COMPANY.phone}</a></div>
            <div><span className="text-slate-400">Email: </span><a className="text-blue-800 hover:underline" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></div>
            <div className="text-slate-500 text-sm pt-1">{COMPANY.hours}</div>
          </div>
          <Link to="/contact" className="mt-5 inline-flex rounded-md bg-blue-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-900">
            Go to Contact
          </Link>
        </div>
      </section>
    </Layout>
  );
}
